import { ApiClient } from '@api/api-configuration';

import type { FolderFile } from './clientCard.model';

/** One file's identity in the folder, which merges the client's files and its visits'. */
export const folderFileKey = (file: FolderFile): string => `${file.source.type}-${file.attachment.attachmentId}`;

/**
 * Base64 to a blob, decoded by the browser rather than in JavaScript.
 *
 * Reading a data: URL hands the whole job to the browser: it decodes natively and
 * the blob it gives back is the browser's own, so saving it does not wait on this
 * page. Measured in Chrome on 12MB: 47ms here, against 110ms for an indexed loop and
 * ~800ms for `Uint8Array.from(binary, (c) => c.charCodeAt(0))`, which is what this
 * used to do — and the loop still leaves a multi-megabyte array and string for the
 * collector, which is main-thread time after the request has already finished.
 */
const base64ToBlob = async (base64: string, contentType?: string | null): Promise<Blob> => {
  const type = contentType || 'application/octet-stream';
  try {
    return await (await fetch(`data:${type};base64,${base64}`)).blob();
  } catch {
    // A content policy can refuse data: URLs. Decoding here is slower but always works.
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
    return new Blob([bytes], { type });
  }
};

const requestFolderFileBlob = async (file: FolderFile): Promise<Blob> => {
  const { attachment, source } = file;
  const response =
    source.type === 'client'
      ? await ApiClient.clientApi.clients.downloadClientAttachment(source.clientId, attachment.attachmentId)
      : await ApiClient.visitsApi.visits.downloadVisitAttachment(source.visitId, attachment.attachmentId);

  return base64ToBlob(response.data.content || '', attachment.contentType);
};

/**
 * Bytes fetched while the card is open, so a file is pulled over the wire once.
 *
 * The endpoint returns the whole file base64 inside JSON, which is the slow part of
 * a download and costs a third more than the file itself. Looking at a photo in the
 * viewer and then saving it used to pay that twice, as did saving the same file
 * again. Entries are dropped oldest-first past the budget, because a folder of scans
 * is bigger than anything worth holding in a tab.
 */
const blobs = new Map<string, Promise<Blob>>();
const sizes = new Map<string, number>();
const CACHE_BUDGET_BYTES = 48 * 1024 * 1024;

/** Forget the fetched bytes. For tests, and for a caller that wants them refetched. */
export const clearFolderFileCache = () => {
  blobs.clear();
  sizes.clear();
};

const evictWhileOverBudget = () => {
  let held = [...sizes.values()].reduce((total, size) => total + size, 0);
  for (const [key, size] of sizes) {
    if (held <= CACHE_BUDGET_BYTES) return;
    blobs.delete(key);
    sizes.delete(key);
    held -= size;
  }
};

/**
 * One file's bytes, from whichever side of the folder it hangs off.
 *
 * The folder merges two sources and the endpoints differ — a visit's attachment is
 * only reachable through its visit — so the file carries where it came from and the
 * request dispatches on it. Getting that wrong is a 404 on a file the reader can see.
 */
export const fetchFolderFileBlob = (file: FolderFile): Promise<Blob> => {
  const key = folderFileKey(file);
  const cached = blobs.get(key);
  if (cached) return cached;

  const pending = requestFolderFileBlob(file)
    .then((blob) => {
      sizes.set(key, blob.size);
      evictWhileOverBudget();
      return blob;
    })
    .catch((error) => {
      // A failed fetch must not be remembered as this file's bytes: the next click
      // has to reach the API again rather than replay the error.
      blobs.delete(key);
      sizes.delete(key);
      throw error;
    });

  blobs.set(key, pending);
  return pending;
};

/** Whether the file is one the image viewer can show. */
export const isFolderImage = (file: FolderFile): boolean => (file.attachment.contentType ?? '').startsWith('image/');

export const isFolderPdf = (file: FolderFile): boolean =>
  (file.attachment.contentType ?? '').toLowerCase() === 'application/pdf' ||
  /\.pdf$/i.test(file.attachment.filename ?? '');

/**
 * A PDF in a new tab, in the browser's own viewer.
 *
 * The tab is opened before the bytes arrive: one opened after the await is no longer
 * part of the click, and the browser blocks it as a popup. The URL is not revoked,
 * because the tab reads it for as long as it stays open.
 */
export const openFolderPdf = async (file: FolderFile): Promise<void> => {
  const tab = window.open('', '_blank');
  try {
    const blob = await fetchFolderFileBlob(file);
    const url = URL.createObjectURL(
      blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' })
    );
    if (!tab) {
      window.open(url, '_blank');
      return;
    }
    // Framed rather than navigated to: a blob URL's tab is titled with its random id,
    // and this page can carry the file's name instead.
    const doc = tab.document;
    doc.title = file.attachment.filename || 'PDF';
    doc.body.style.margin = '0';
    const frame = doc.createElement('iframe');
    frame.src = url;
    frame.title = doc.title;
    frame.style.cssText = 'border:0;width:100vw;height:100vh;display:block';
    doc.body.replaceChildren(frame);
  } catch (error) {
    tab?.close();
    throw error;
  }
};
